import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import {
  Plus, Building2, Users, Target, FileText, Activity, Shield,
  ToggleLeft, ToggleRight, Sparkles, KeyRound, Clock, Trash2,
  Search, Phone, Mail, Briefcase, Edit2, Check, X
} from 'lucide-react';

const PLAN_COLORS = {
  trial: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
  basic: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
  pro: 'bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-300',
  enterprise: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300'
};

const SALES_STATUS_COLORS = {
  new: 'bg-blue-100 text-blue-700',
  contacted: 'bg-amber-100 text-amber-700',
  demo: 'bg-violet-100 text-violet-700',
  negotiation: 'bg-cyan-100 text-cyan-700',
  closed_won: 'bg-emerald-100 text-emerald-700',
  closed_lost: 'bg-red-100 text-red-700'
};

export default function CompanyManagement() {
  const { user } = useAuth();
  const [systemStats, setSystemStats] = useState(null);
  const [auditLogs, setAuditLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [showResetPw, setShowResetPw] = useState(false);
  const [resetUserId, setResetUserId] = useState('');
  const [resetUserEmail, setResetUserEmail] = useState('');
  const [resetNewPw, setResetNewPw] = useState('');
  const [resetCompanyUsers, setResetCompanyUsers] = useState([]);
  const [resetCompanyName, setResetCompanyName] = useState('');
  const [newCompany, setNewCompany] = useState({ name: '', domain: '', subscription_plan: 'trial', max_users: 10 });
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Sales CRM state
  const [salesLeads, setSalesLeads] = useState([]);
  const [salesLoading, setSalesLoading] = useState(false);
  const [showAddSalesLead, setShowAddSalesLead] = useState(false);
  const [salesSearch, setSalesSearch] = useState('');
  const [salesFilter, setSalesFilter] = useState('all');
  const [newSalesLead, setNewSalesLead] = useState({
    company_name: '', contact_person: '', email: '', phone: '',
    source: 'direct', status: 'new', notes: '', expected_plan: ''
  });
  const [editingSalesLead, setEditingSalesLead] = useState(null);
  const [editSalesData, setEditSalesData] = useState({});

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [statsRes, logsRes] = await Promise.all([
        api.get('/admin/system-stats'),
        api.get('/admin/audit-logs?limit=50')
      ]);
      setSystemStats(statsRes.data);
      setAuditLogs(logsRes.data);
    } catch {
      toast.error('Failed to load admin data');
    } finally {
      setLoading(false);
    }
  };

  const fetchSalesLeads = async () => {
    setSalesLoading(true);
    try {
      let url = '/admin/sales-leads';
      const params = [];
      if (salesFilter !== 'all') params.push(`status=${salesFilter}`);
      if (salesSearch.trim()) params.push(`search=${encodeURIComponent(salesSearch.trim())}`);
      if (params.length) url += '?' + params.join('&');
      const res = await api.get(url);
      setSalesLeads(res.data);
    } catch {
      toast.error('Failed to load sales leads');
    } finally {
      setSalesLoading(false);
    }
  };

  useEffect(() => { fetchSalesLeads(); }, [salesFilter, salesSearch]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!newCompany.name) { toast.error('Company name required'); return; }
    try {
      await api.post('/companies', newCompany);
      toast.success('Company created');
      setShowCreate(false);
      setNewCompany({ name: '', domain: '', subscription_plan: 'trial', max_users: 10 });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create company');
    }
  };

  const toggleCompanyActive = async (companyId, currentActive) => {
    try {
      await api.put(`/admin/company/${companyId}/settings`, { is_active: !currentActive });
      toast.success(currentActive ? 'Company deactivated' : 'Company activated');
      fetchData();
    } catch { toast.error('Failed to update'); }
  };

  const toggleAIAccess = async (companyId, currentEnabled) => {
    try {
      await api.put(`/admin/company/${companyId}/settings`, { ai_itinerary_enabled: !currentEnabled });
      toast.success(!currentEnabled ? 'AI Itinerary enabled' : 'AI Itinerary disabled');
      fetchData();
    } catch { toast.error('Failed to update'); }
  };

  const updateField = async (companyId, field, value) => {
    try {
      await api.put(`/admin/company/${companyId}/settings`, { [field]: value });
      toast.success('Updated successfully');
      fetchData();
    } catch { toast.error('Failed to update'); }
  };

  const updatePlan = async (companyId, plan) => {
    try {
      await api.put(`/admin/company/${companyId}/settings`, { subscription_plan: plan });
      toast.success('Plan updated');
      fetchData();
    } catch { toast.error('Failed to update'); }
  };

  const handleDeleteCompany = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await api.delete(`/companies/${deleteTarget.id}`);
      toast.success(`Company "${deleteTarget.name}" deleted`);
      setDeleteTarget(null);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    } finally {
      setDeleting(false);
    }
  };

  const handleAdminResetPassword = async () => {
    if (!resetUserId) { toast.error('Select a user'); return; }
    if (!resetNewPw || resetNewPw.length < 6) { toast.error('Password must be at least 6 characters'); return; }
    try {
      await api.post('/admin/reset-user-password', { user_id: resetUserId, new_password: resetNewPw });
      toast.success(`Password reset for ${resetUserEmail}`);
      setShowResetPw(false);
      setResetNewPw('');
      setResetUserId('');
      setResetCompanyUsers([]);
    } catch (err) { toast.error(err.response?.data?.detail || 'Reset failed'); }
  };

  const openResetPwForCompany = async (companyId, companyName) => {
    setResetCompanyName(companyName);
    setResetUserId('');
    setResetUserEmail('');
    setResetNewPw('');
    try {
      const res = await api.get(`/users?company_id=${companyId}`);
      setResetCompanyUsers(res.data);
    } catch { setResetCompanyUsers([]); }
    setShowResetPw(true);
  };

  // Sales CRM handlers
  const handleAddSalesLead = async (e) => {
    e.preventDefault();
    if (!newSalesLead.company_name) { toast.error('Company name is required'); return; }
    try {
      await api.post('/admin/sales-leads', newSalesLead);
      toast.success('Sales lead added');
      setShowAddSalesLead(false);
      setNewSalesLead({ company_name: '', contact_person: '', email: '', phone: '', source: 'direct', status: 'new', notes: '', expected_plan: '' });
      fetchSalesLeads();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add lead');
    }
  };

  const startEditSalesLead = (lead) => {
    setEditingSalesLead(lead.id);
    setEditSalesData({ ...lead });
  };

  const cancelEditSalesLead = () => {
    setEditingSalesLead(null);
    setEditSalesData({});
  };

  const saveEditSalesLead = async () => {
    try {
      await api.put(`/admin/sales-leads/${editingSalesLead}`, editSalesData);
      toast.success('Lead updated');
      setEditingSalesLead(null);
      fetchSalesLeads();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update');
    }
  };

  const deleteSalesLead = async (leadId) => {
    try {
      await api.delete(`/admin/sales-leads/${leadId}`);
      toast.success('Lead deleted');
      fetchSalesLeads();
    } catch { toast.error('Failed to delete'); }
  };

  if (user?.role !== 'super_admin') {
    return <div className="flex items-center justify-center h-64"><p className="text-muted-foreground">Access denied. Super Admin only.</p></div>;
  }

  if (loading) return <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>;

  const stats = systemStats || {};

  const salesStatusCounts = salesLeads.reduce((acc, l) => {
    acc[l.status] = (acc[l.status] || 0) + 1;
    return acc;
  }, {});

  return (
    <div className="space-y-5 animate-fade-in" data-testid="company-management-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground tracking-tight">Admin Panel</h1>
          <p className="text-sm text-muted-foreground mt-0.5">Manage companies, users, sales pipeline and platform settings</p>
        </div>
        <Dialog open={showCreate} onOpenChange={setShowCreate}>
          <DialogTrigger asChild>
            <Button className="gap-2 bg-voyage-blue hover:bg-voyage-blue-dark text-white" data-testid="create-company-btn">
              <Plus className="w-4 h-4" /> Add Company
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle className="font-heading">Create New Company</DialogTitle><DialogDescription>Register a new travel agency on the platform.</DialogDescription></DialogHeader>
            <form onSubmit={handleCreate} className="space-y-4" data-testid="create-company-form">
              <div><Label>Company Name *</Label><Input value={newCompany.name} onChange={e => setNewCompany({...newCompany, name: e.target.value})} className="mt-1" data-testid="company-name-input" /></div>
              <div><Label>Domain</Label><Input value={newCompany.domain} onChange={e => setNewCompany({...newCompany, domain: e.target.value})} className="mt-1" placeholder="example.com" /></div>
              <div><Label>Plan</Label>
                <Select value={newCompany.subscription_plan} onValueChange={v => setNewCompany({...newCompany, subscription_plan: v})}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="trial">Trial (15 days)</SelectItem><SelectItem value="basic">Basic</SelectItem>
                    <SelectItem value="pro">Pro</SelectItem><SelectItem value="enterprise">Enterprise</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Max Users</Label><Input type="number" min={1} value={newCompany.max_users} onChange={e => setNewCompany({...newCompany, max_users: parseInt(e.target.value) || 10})} className="mt-1" /></div>
              <Button type="submit" className="w-full bg-voyage-blue hover:bg-voyage-blue-dark text-white" data-testid="submit-company-btn">Create Company</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* System Summary Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="admin-stats">
        {[
          { label: 'Companies', value: stats.total_companies, icon: Building2, color: 'text-violet-500', bg: 'bg-violet-500/10' },
          { label: 'Active', value: stats.active_companies, icon: ToggleRight, color: 'text-emerald-500', bg: 'bg-emerald-500/10' },
          { label: 'Users', value: stats.total_users, icon: Users, color: 'text-blue-500', bg: 'bg-blue-500/10' },
          { label: 'Leads', value: stats.total_leads, icon: Target, color: 'text-cyan-500', bg: 'bg-cyan-500/10' },
          { label: 'Itineraries', value: stats.total_itineraries, icon: FileText, color: 'text-voyage-orange', bg: 'bg-voyage-orange/10' },
          { label: 'Revenue', value: `${(stats.total_revenue || 0).toLocaleString()}`, icon: Activity, color: 'text-emerald-500', bg: 'bg-emerald-500/10' }
        ].map((s) => (
          <Card key={s.label} className="border border-border">
            <CardContent className="p-3 flex items-center gap-2">
              <div className={`w-8 h-8 rounded-lg ${s.bg} flex items-center justify-center flex-shrink-0`}><s.icon className={`w-4 h-4 ${s.color}`} /></div>
              <div><p className="text-[10px] text-muted-foreground uppercase">{s.label}</p><p className="font-heading font-bold text-lg">{s.value || 0}</p></div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Tabs defaultValue="companies" className="space-y-4">
        <TabsList className="bg-muted/50">
          <TabsTrigger value="companies" className="text-sm" data-testid="tab-companies">Companies ({stats.total_companies})</TabsTrigger>
          <TabsTrigger value="sales-crm" className="text-sm" data-testid="tab-sales-crm">Sales CRM ({salesLeads.length})</TabsTrigger>
          <TabsTrigger value="activity" className="text-sm" data-testid="tab-activity">Activity Logs</TabsTrigger>
        </TabsList>

        {/* Companies Tab */}
        <TabsContent value="companies">
          <Card className="border border-border bg-card">
            <CardContent className="p-0 overflow-x-auto">
              <Table data-testid="companies-table">
                <TableHeader>
                  <TableRow>
                    <TableHead className="font-heading">Company</TableHead>
                    <TableHead className="font-heading">Plan</TableHead>
                    <TableHead className="font-heading text-center">Active</TableHead>
                    <TableHead className="font-heading text-center">AI Access</TableHead>
                    <TableHead className="font-heading text-center">AI Limit/mo</TableHead>
                    <TableHead className="font-heading text-center">Users</TableHead>
                    <TableHead className="font-heading text-center">Doc DL Limit</TableHead>
                    <TableHead className="font-heading text-center">Leads</TableHead>
                    <TableHead className="font-heading text-center">Itineraries</TableHead>
                    <TableHead className="font-heading">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(stats.company_stats || []).map(cs => {
                    const c = cs.company;
                    return (
                      <TableRow key={c.id} data-testid={`company-row-${c.id}`}>
                        <TableCell>
                          <div><span className="font-medium text-sm">{c.name}</span><p className="text-xs text-muted-foreground">{c.domain || '-'}</p></div>
                        </TableCell>
                        <TableCell>
                          <Select value={c.subscription_plan} onValueChange={(v) => updatePlan(c.id, v)}>
                            <SelectTrigger className="w-28 h-7 text-xs" data-testid={`plan-select-${c.id}`}><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="trial">Trial</SelectItem><SelectItem value="basic">Basic</SelectItem>
                              <SelectItem value="pro">Pro</SelectItem><SelectItem value="enterprise">Enterprise</SelectItem>
                            </SelectContent>
                          </Select>
                        </TableCell>
                        <TableCell className="text-center">
                          <Switch checked={c.is_active !== false} onCheckedChange={() => toggleCompanyActive(c.id, c.is_active !== false)}
                            data-testid={`toggle-active-${c.id}`} />
                        </TableCell>
                        <TableCell className="text-center">
                          <Switch checked={c.ai_itinerary_enabled !== false} onCheckedChange={() => toggleAIAccess(c.id, c.ai_itinerary_enabled !== false)}
                            data-testid={`toggle-ai-${c.id}`} />
                        </TableCell>
                        <TableCell className="text-center">
                          <Input type="number" min={-1} defaultValue={c.ai_monthly_limit ?? -1} className="w-16 h-6 text-xs text-center p-0"
                            onBlur={(e) => updateField(c.id, 'ai_monthly_limit', parseInt(e.target.value) || -1)}
                            title="-1 = unlimited"
                            data-testid={`ai-limit-${c.id}`} />
                          <p className="text-[9px] text-muted-foreground mt-0.5">{(c.ai_monthly_limit ?? -1) === -1 ? 'Unlimited' : ''}</p>
                        </TableCell>
                        <TableCell className="text-center">
                          <div className="flex items-center justify-center gap-1">
                            <span className="text-sm">{cs.users_count}</span>
                            <span className="text-xs text-muted-foreground">/</span>
                            <Input type="number" min={1} defaultValue={c.max_users || 10} className="w-14 h-6 text-xs text-center p-0"
                              onBlur={(e) => updateField(c.id, 'max_users', parseInt(e.target.value) || 10)}
                              data-testid={`max-users-${c.id}`} />
                          </div>
                        </TableCell>
                        <TableCell className="text-center">
                          <Input type="number" min={-1} defaultValue={c.doc_download_limit ?? -1} className="w-16 h-6 text-xs text-center p-0"
                            onBlur={(e) => updateField(c.id, 'doc_download_limit', parseInt(e.target.value) || -1)}
                            title="-1 = unlimited"
                            data-testid={`doc-dl-limit-${c.id}`} />
                          <p className="text-[9px] text-muted-foreground mt-0.5">{(c.doc_download_limit ?? -1) === -1 ? 'Unlimited' : ''}</p>
                        </TableCell>
                        <TableCell className="text-center text-sm">{cs.leads_count}</TableCell>
                        <TableCell className="text-center text-sm">{cs.itineraries_count}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            <Button variant="ghost" size="sm" className="text-xs gap-1" onClick={() => openResetPwForCompany(c.id, c.name)}
                              data-testid={`reset-pw-${c.id}`}>
                              <KeyRound className="w-3 h-3" /> Reset PW
                            </Button>
                            <Button variant="ghost" size="sm" className="text-xs gap-1 text-red-500 hover:text-red-700 hover:bg-red-50"
                              onClick={() => setDeleteTarget({ id: c.id, name: c.name })}
                              data-testid={`delete-company-${c.id}`}>
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Sales CRM Tab */}
        <TabsContent value="sales-crm">
          <div className="space-y-4">
            {/* Sales Stats */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
              {[
                { label: 'Total', count: salesLeads.length, color: 'bg-slate-100 text-slate-700' },
                { label: 'New', count: salesStatusCounts.new || 0, color: 'bg-blue-100 text-blue-700' },
                { label: 'Contacted', count: salesStatusCounts.contacted || 0, color: 'bg-amber-100 text-amber-700' },
                { label: 'Demo', count: salesStatusCounts.demo || 0, color: 'bg-violet-100 text-violet-700' },
                { label: 'Negotiation', count: salesStatusCounts.negotiation || 0, color: 'bg-cyan-100 text-cyan-700' },
                { label: 'Won', count: salesStatusCounts.closed_won || 0, color: 'bg-emerald-100 text-emerald-700' },
              ].map(s => (
                <div key={s.label} className={`rounded-lg px-3 py-2 ${s.color}`}>
                  <p className="text-xs font-medium opacity-70">{s.label}</p>
                  <p className="text-xl font-bold">{s.count}</p>
                </div>
              ))}
            </div>

            {/* Search + Add */}
            <div className="flex items-center gap-3">
              <div className="relative flex-1">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input placeholder="Search by company, person, email..." className="pl-9"
                  value={salesSearch} onChange={e => setSalesSearch(e.target.value)}
                  data-testid="sales-search" />
              </div>
              <Select value={salesFilter} onValueChange={setSalesFilter}>
                <SelectTrigger className="w-36" data-testid="sales-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="new">New</SelectItem>
                  <SelectItem value="contacted">Contacted</SelectItem>
                  <SelectItem value="demo">Demo</SelectItem>
                  <SelectItem value="negotiation">Negotiation</SelectItem>
                  <SelectItem value="closed_won">Closed Won</SelectItem>
                  <SelectItem value="closed_lost">Closed Lost</SelectItem>
                </SelectContent>
              </Select>
              <Dialog open={showAddSalesLead} onOpenChange={setShowAddSalesLead}>
                <DialogTrigger asChild>
                  <Button className="gap-2 bg-voyage-blue hover:bg-voyage-blue-dark text-white" data-testid="add-sales-lead-btn">
                    <Plus className="w-4 h-4" /> Add Lead
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-lg">
                  <DialogHeader>
                    <DialogTitle className="font-heading">Add Sales Lead</DialogTitle>
                    <DialogDescription>Track a new company approaching VoyageOS.</DialogDescription>
                  </DialogHeader>
                  <form onSubmit={handleAddSalesLead} className="space-y-3" data-testid="add-sales-lead-form">
                    <div className="grid grid-cols-2 gap-3">
                      <div><Label>Company Name *</Label><Input value={newSalesLead.company_name} onChange={e => setNewSalesLead({...newSalesLead, company_name: e.target.value})} className="mt-1" data-testid="sales-company-name" /></div>
                      <div><Label>Contact Person</Label><Input value={newSalesLead.contact_person} onChange={e => setNewSalesLead({...newSalesLead, contact_person: e.target.value})} className="mt-1" /></div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div><Label>Email</Label><Input type="email" value={newSalesLead.email} onChange={e => setNewSalesLead({...newSalesLead, email: e.target.value})} className="mt-1" /></div>
                      <div><Label>Phone</Label><Input value={newSalesLead.phone} onChange={e => setNewSalesLead({...newSalesLead, phone: e.target.value})} className="mt-1" /></div>
                    </div>
                    <div className="grid grid-cols-3 gap-3">
                      <div><Label>Source</Label>
                        <Select value={newSalesLead.source} onValueChange={v => setNewSalesLead({...newSalesLead, source: v})}>
                          <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="direct">Direct</SelectItem><SelectItem value="referral">Referral</SelectItem>
                            <SelectItem value="website">Website</SelectItem><SelectItem value="social">Social Media</SelectItem>
                            <SelectItem value="event">Event</SelectItem><SelectItem value="other">Other</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div><Label>Status</Label>
                        <Select value={newSalesLead.status} onValueChange={v => setNewSalesLead({...newSalesLead, status: v})}>
                          <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="new">New</SelectItem><SelectItem value="contacted">Contacted</SelectItem>
                            <SelectItem value="demo">Demo</SelectItem><SelectItem value="negotiation">Negotiation</SelectItem>
                            <SelectItem value="closed_won">Closed Won</SelectItem><SelectItem value="closed_lost">Closed Lost</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div><Label>Expected Plan</Label>
                        <Select value={newSalesLead.expected_plan || 'none'} onValueChange={v => setNewSalesLead({...newSalesLead, expected_plan: v === 'none' ? '' : v})}>
                          <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">-</SelectItem>
                            <SelectItem value="starter">Starter</SelectItem><SelectItem value="professional">Professional</SelectItem>
                            <SelectItem value="enterprise">Enterprise</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div><Label>Notes</Label><Textarea value={newSalesLead.notes} onChange={e => setNewSalesLead({...newSalesLead, notes: e.target.value})} className="mt-1" rows={2} /></div>
                    <Button type="submit" className="w-full bg-voyage-blue hover:bg-voyage-blue-dark text-white" data-testid="submit-sales-lead-btn">Add Sales Lead</Button>
                  </form>
                </DialogContent>
              </Dialog>
            </div>

            {/* Sales Leads Table */}
            <Card className="border border-border bg-card">
              <CardContent className="p-0 overflow-x-auto">
                <Table data-testid="sales-leads-table">
                  <TableHeader>
                    <TableRow>
                      <TableHead className="font-heading">Company</TableHead>
                      <TableHead className="font-heading">Contact</TableHead>
                      <TableHead className="font-heading">Source</TableHead>
                      <TableHead className="font-heading">Status</TableHead>
                      <TableHead className="font-heading">Expected Plan</TableHead>
                      <TableHead className="font-heading">Notes</TableHead>
                      <TableHead className="font-heading">Date</TableHead>
                      <TableHead className="font-heading">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {salesLeads.length === 0 ? (
                      <TableRow><TableCell colSpan={8} className="text-center text-muted-foreground py-8">No sales leads yet. Add your first lead above.</TableCell></TableRow>
                    ) : salesLeads.map(lead => (
                      <TableRow key={lead.id} data-testid={`sales-lead-row-${lead.id}`}>
                        {editingSalesLead === lead.id ? (
                          <>
                            <TableCell><Input value={editSalesData.company_name || ''} onChange={e => setEditSalesData({...editSalesData, company_name: e.target.value})} className="h-7 text-xs" /></TableCell>
                            <TableCell>
                              <div className="space-y-1">
                                <Input value={editSalesData.contact_person || ''} onChange={e => setEditSalesData({...editSalesData, contact_person: e.target.value})} className="h-7 text-xs" placeholder="Name" />
                                <Input value={editSalesData.email || ''} onChange={e => setEditSalesData({...editSalesData, email: e.target.value})} className="h-7 text-xs" placeholder="Email" />
                                <Input value={editSalesData.phone || ''} onChange={e => setEditSalesData({...editSalesData, phone: e.target.value})} className="h-7 text-xs" placeholder="Phone" />
                              </div>
                            </TableCell>
                            <TableCell>
                              <Select value={editSalesData.source || 'direct'} onValueChange={v => setEditSalesData({...editSalesData, source: v})}>
                                <SelectTrigger className="h-7 text-xs w-24"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="direct">Direct</SelectItem><SelectItem value="referral">Referral</SelectItem>
                                  <SelectItem value="website">Website</SelectItem><SelectItem value="social">Social</SelectItem>
                                  <SelectItem value="event">Event</SelectItem><SelectItem value="other">Other</SelectItem>
                                </SelectContent>
                              </Select>
                            </TableCell>
                            <TableCell>
                              <Select value={editSalesData.status || 'new'} onValueChange={v => setEditSalesData({...editSalesData, status: v})}>
                                <SelectTrigger className="h-7 text-xs w-28"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="new">New</SelectItem><SelectItem value="contacted">Contacted</SelectItem>
                                  <SelectItem value="demo">Demo</SelectItem><SelectItem value="negotiation">Negotiation</SelectItem>
                                  <SelectItem value="closed_won">Closed Won</SelectItem><SelectItem value="closed_lost">Closed Lost</SelectItem>
                                </SelectContent>
                              </Select>
                            </TableCell>
                            <TableCell>
                              <Select value={editSalesData.expected_plan || 'none'} onValueChange={v => setEditSalesData({...editSalesData, expected_plan: v === 'none' ? '' : v})}>
                                <SelectTrigger className="h-7 text-xs w-28"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="none">-</SelectItem>
                                  <SelectItem value="starter">Starter</SelectItem><SelectItem value="professional">Professional</SelectItem>
                                  <SelectItem value="enterprise">Enterprise</SelectItem>
                                </SelectContent>
                              </Select>
                            </TableCell>
                            <TableCell><Input value={editSalesData.notes || ''} onChange={e => setEditSalesData({...editSalesData, notes: e.target.value})} className="h-7 text-xs" /></TableCell>
                            <TableCell className="text-xs text-muted-foreground">{new Date(lead.created_at).toLocaleDateString()}</TableCell>
                            <TableCell>
                              <div className="flex items-center gap-1">
                                <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-emerald-600 hover:bg-emerald-50" onClick={saveEditSalesLead} data-testid={`save-edit-${lead.id}`}><Check className="w-3.5 h-3.5" /></Button>
                                <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-muted-foreground hover:bg-muted" onClick={cancelEditSalesLead}><X className="w-3.5 h-3.5" /></Button>
                              </div>
                            </TableCell>
                          </>
                        ) : (
                          <>
                            <TableCell><span className="font-medium text-sm">{lead.company_name}</span></TableCell>
                            <TableCell>
                              <div className="text-xs space-y-0.5">
                                {lead.contact_person && <p className="font-medium">{lead.contact_person}</p>}
                                {lead.email && <p className="text-muted-foreground flex items-center gap-1"><Mail className="w-3 h-3" />{lead.email}</p>}
                                {lead.phone && <p className="text-muted-foreground flex items-center gap-1"><Phone className="w-3 h-3" />{lead.phone}</p>}
                              </div>
                            </TableCell>
                            <TableCell><span className="text-xs capitalize">{lead.source}</span></TableCell>
                            <TableCell>
                              <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${SALES_STATUS_COLORS[lead.status] || 'bg-gray-100 text-gray-700'}`}>
                                {lead.status?.replace('_', ' ')}
                              </span>
                            </TableCell>
                            <TableCell><span className="text-xs capitalize">{lead.expected_plan || '-'}</span></TableCell>
                            <TableCell><p className="text-xs text-muted-foreground max-w-[150px] truncate" title={lead.notes}>{lead.notes || '-'}</p></TableCell>
                            <TableCell className="text-xs text-muted-foreground">{new Date(lead.created_at).toLocaleDateString()}</TableCell>
                            <TableCell>
                              <div className="flex items-center gap-1">
                                <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => startEditSalesLead(lead)} data-testid={`edit-sales-${lead.id}`}><Edit2 className="w-3.5 h-3.5" /></Button>
                                <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-500 hover:text-red-700 hover:bg-red-50" onClick={() => deleteSalesLead(lead.id)} data-testid={`delete-sales-${lead.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                              </div>
                            </TableCell>
                          </>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Activity Logs Tab */}
        <TabsContent value="activity">
          <Card className="border border-border bg-card">
            <CardHeader className="pb-2">
              <CardTitle className="font-heading text-base flex items-center gap-2">
                <Activity className="w-4 h-4 text-primary" /> Recent Activity
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[400px]">
                <div className="space-y-2" data-testid="audit-logs-list">
                  {auditLogs.length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-8">No activity logged yet</p>
                  ) : auditLogs.map((log, i) => (
                    <div key={log.id || `log-${i}`} className="flex items-start gap-3 p-2 rounded-lg hover:bg-muted/50 border-b border-border/30 last:border-0">
                      <div className="w-7 h-7 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Clock className="w-3 h-3 text-primary" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium capitalize">{log.action?.replace(/_/g, ' ')}</p>
                        <p className="text-xs text-muted-foreground">
                          {log.entity_type} - {log.details ? JSON.stringify(log.details).substring(0, 80) : ''}
                        </p>
                        <p className="text-[10px] text-muted-foreground mt-0.5">{new Date(log.created_at).toLocaleString()}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Delete Company Confirmation Dialog */}
      <Dialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading text-red-600">Delete Company</DialogTitle>
            <DialogDescription>
              This will permanently delete <strong>{deleteTarget?.name}</strong> and all its data including users, leads, itineraries, follow-ups, and payments. This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setDeleteTarget(null)} data-testid="cancel-delete-btn">Cancel</Button>
            <Button variant="destructive" onClick={handleDeleteCompany} disabled={deleting}
              data-testid="confirm-delete-btn">
              {deleting ? 'Deleting...' : 'Delete Company'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Admin Reset Password Dialog */}
      <Dialog open={showResetPw} onOpenChange={setShowResetPw}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading">Reset User Password</DialogTitle>
            <DialogDescription>{resetCompanyName ? `Select a user from ${resetCompanyName}` : 'Select a user and set new password.'}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Select User</Label>
              <Select value={resetUserId} onValueChange={(v) => {
                setResetUserId(v);
                const u = resetCompanyUsers.find(u => u.id === v);
                if (u) setResetUserEmail(u.email);
              }}>
                <SelectTrigger className="mt-1" data-testid="admin-reset-user-select"><SelectValue placeholder="Choose user..." /></SelectTrigger>
                <SelectContent>
                  {resetCompanyUsers.map(u => (
                    <SelectItem key={u.id} value={u.id}>{u.name} ({u.email}) - {u.role}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div><Label>New Password</Label><Input type="password" value={resetNewPw} onChange={e => setResetNewPw(e.target.value)} className="mt-1" placeholder="Min 6 characters" data-testid="admin-reset-new-pw" /></div>
            <Button onClick={handleAdminResetPassword} className="w-full bg-voyage-blue hover:bg-voyage-blue-dark text-white" data-testid="admin-reset-submit">Reset Password</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
